// Tracks which project-result sections the user has marked as "reviewed" while
// working through a project analysis step by step. Purely a local progress aid
// — it does NOT touch the backend or influence generation. Persisted to
// localStorage (SSR-safe + quota/private-mode tolerant, mirroring token-store /
// selected-set), keyed by project id → list of reviewed topic slugs. A small
// subscriber set lets React re-render on toggle via useSyncExternalStore.
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "edupause.reviewedSections.v1";

type ReviewedMap = Record<string, string[]>;
type Listener = () => void;

const listeners = new Set<Listener>();
// Bumped on every mutation so per-project snapshots are rebuilt lazily. Bumped
// before notifying, so React's getSnapshot (driven by the same notify) sees it.
let version = 0;

function read(): ReviewedMap {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReviewedMap) : {};
  } catch {
    return {};
  }
}

function write(map: ReviewedMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* private mode / quota — ignore, in-memory toggle still applied this session */
  }
  version += 1;
  for (const l of listeners) l();
}

export function reviewedSlugs(projectId: string): Set<string> {
  return new Set(read()[projectId] ?? []);
}

export function isReviewed(projectId: string, slug: string): boolean {
  return (read()[projectId] ?? []).includes(slug);
}

export function toggle(projectId: string, slug: string): void {
  const map = read();
  const current = new Set(map[projectId] ?? []);
  if (current.has(slug)) current.delete(slug);
  else current.add(slug);
  if (current.size > 0) map[projectId] = [...current];
  else delete map[projectId];
  write(map);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Per-project snapshot, cached so useSyncExternalStore sees a stable reference
// between toggles (it bails on Object.is equality). Rebuilt only when the store
// notifies a change.
const snapshots = new Map<string, Set<string>>();
let lastVersion = -1;

function refreshSnapshots() {
  if (version === lastVersion) return;
  lastVersion = version;
  const map = read();
  snapshots.clear();
  for (const [pid, slugs] of Object.entries(map)) snapshots.set(pid, new Set(slugs));
}

const EMPTY: Set<string> = new Set();

/**
 * React hook: reviewed slugs for one project + a toggle callback.
 * Local-only; re-renders the caller when the set changes.
 */
export function useReviewedSections(projectId: string): {
  reviewed: Set<string>;
  toggleSection: (slug: string) => void;
} {
  const reviewed = useSyncExternalStore(
    subscribe,
    () => {
      refreshSnapshots();
      return snapshots.get(projectId) ?? EMPTY;
    },
    () => EMPTY,
  );
  const toggleSection = useCallback((slug: string) => toggle(projectId, slug), [projectId]);
  return { reviewed, toggleSection };
}
